import { useState } from 'react';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { CaseDetails } from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseDetails';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import {
  BankApplication,
  BankApplicationStatus,
} from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import BankIcon from '@components/v2/BankIcon';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { useNavigate } from '@tanstack/react-router';
import { CaseCreationSubmitApplicationsRoute } from '@modules/opportunities/presentation/routes';
import { ArrowLeft } from '@huspy/briks-icons';
import { StatusTag } from '@components/StatusTag';
import UploadTitleDeed from './UploadTitleDeed';

type CaseProgressionSlidoType = {
  opportunityId: string;
  bankApplications: BankApplication[];
  isOpen: boolean;
  isCaseOwner: boolean;
  close: () => void;
  selectedBankApplicationId?: string | null
};

const baseTranslation = 'opportunity.caseDetails.caseProgression';

export const CaseProgressionSlido = ({
  isOpen,
  opportunityId,
  bankApplications,
  isCaseOwner,
  close,
  selectedBankApplicationId = null,
}: CaseProgressionSlidoType) => {
  const { t } = useTranslation();
  const [bankApplicationId, setBankApplicationId] = useState<string | null>(
    selectedBankApplicationId
  );
  const navigate = useNavigate();

  return (
    <BaseSlido
      title={ (
        <Flex align='center' justify='start' p='8'>
          {bankApplicationId && (
            <Flex
              justify='center'
              align='center'
              w='8'
              h='8'
              mr='2'
              cursor='pointer'
              boxShadow='shadow-4'
              borderRadius='full'
              onClick={ () => setBankApplicationId(null) }
            >
              <ArrowLeft height={ 20 } width={ 20 } />
            </Flex>
          )}
          <Text style={ { fontSize: '32px' } } weight='semiBold'>
            {t('opportunity.caseDetails.title')}
          </Text>
        </Flex>
      ) }
      opened={ isOpen }
      close={ () => {
        close();
        setBankApplicationId(null);
      } }
      size='xl'
    >
      <Box p='8'>
        {!bankApplicationId && (
          <>
            <Text size='xl' fontWeight='semibold'>
              {t(`${baseTranslation}.title`)}
            </Text>
            <Text size='md' color='neutral.500'>
              {t(`${baseTranslation}.subtitle`)}
            </Text>
            <Flex direction='column' mt='4' gap='8'>
              {bankApplications.map((bankApplication) => {
                const isDraft = bankApplication.status === BANK_APPLICATION_STATUS.draft;
                const titleDeedEligibleStatuses: BankApplicationStatus[] = [
                  BANK_APPLICATION_STATUS.disbursed,
                  BANK_APPLICATION_STATUS.propertyTransferred
                ];
                const validTitleDeedStatus = titleDeedEligibleStatuses.includes(bankApplication.status);
                const shouldUploadTitleDeed = validTitleDeedStatus && isCaseOwner;
                const variant = isDraft ? 'primary' : 'secondary';
                const onClick = () => {
                  if (isDraft) {
                    navigate({
                      to: CaseCreationSubmitApplicationsRoute.to,
                      params: { opportunityId },
                      search: { bank_application_id: '' },
                    });
                  } else {
                    setBankApplicationId(
                      bankApplication.bank_application_external_id
                    );
                  }
                };
                const buttonText = isDraft
                  ? t(`${baseTranslation}.continueBtn`)
                  : t(`${baseTranslation}.viewDetailsBtn`);
                return (
                  <Flex
                    key={ bankApplication.bank_application_external_id }
                    justify='space-between'
                    align='center'
                  >
                    <Flex align='center' gap='2'>
                      <Flex>
                        <BankIcon
                          icon={ bankApplication?.bank_icon! ?? bankApplication?.bank_details?.icon }
                          hasShadow
                          width='52'
                          height='52'
                        />
                      </Flex>
                      <Flex direction='column' gap='1'>
                        <Text size='lg'>{bankApplication.bank_name ?? bankApplication?.bank_details?.name}</Text>
                        <StatusTag status={ bankApplication.status } />
                      </Flex>
                    </Flex>
                    <Flex gap='2'>
                      <UploadTitleDeed
                        opportunityId={ opportunityId }
                        bankApplicationExternalId={ bankApplication.bank_application_external_id }
                        shouldUploadTitleDeed={ shouldUploadTitleDeed }
                        buttonText={ t(`${baseTranslation}.titleDeed.uploadBtn`) }
                      />
                      <Button
                        w='fit-content'
                        variant={ variant }
                        onClick={ onClick }
                        size='sm'
                      >
                        {buttonText}
                      </Button>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          </>
        )}
        {bankApplicationId && (
          <CaseDetails
            bankApplicationId={ bankApplicationId }
            opportunityId={ opportunityId }
          />
        )}
      </Box>
    </BaseSlido>
  );
};
